/**
 * Sprint B / B-6 — logout sweep for clinical keys in browser storage
 * (deep-audit F-05).
 *
 * Until B-6, three writers put clinical step data at rest in localStorage
 * (`autosave:<key>`, `msk_complaints_<assessmentId>`,
 * `autosave:physExam_<assessmentId>`) with no reader and no clearer, so the
 * data survived logout and browser restart. The writes are gone; this sweep
 * removes whatever a browser still holds from before the change, and it runs
 * on every path that ends the session (explicit logout, the mount-time
 * /auth/me 401, the api-layer and SSE 401 handlers via clearAuthToken, and
 * the patient-portal logout, which shares the origin).
 *
 * `msk_assessment_<id>` is a step cursor ({ assessmentId, currentStep,
 * timestamp } — no clinical content) with live crash-recovery readers; it is
 * still written, but swept here so nothing assessment-linked outlives the
 * session on a shared machine.
 *
 * The allowlist of keys that MAY be written lives in the pin:
 * src/__tests__/sprint-b-6-no-clinical-localstorage.test.ts
 */

export const SWEPT_KEY_PREFIXES: ReadonlyArray<string> = [
  'autosave:',
  'msk_complaints_',
  'msk_assessment_',
];

function defaultStorage(): Storage | undefined {
  try { return typeof localStorage === 'undefined' ? undefined : localStorage; } catch { return undefined; }
}

/**
 * Remove every key that starts with one of SWEPT_KEY_PREFIXES.
 * Returns the removed keys (for logging/tests). Never throws — storage may be
 * unavailable (private mode, SecurityError) and logout must still complete.
 */
export function sweepClinicalStorage(storage: Storage | undefined = defaultStorage()): string[] {
  const removed: string[] = [];
  if (!storage) return removed;
  try {
    // Collect first: removing while iterating by index skips keys.
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && SWEPT_KEY_PREFIXES.some((p) => k.startsWith(p))) doomed.push(k);
    }
    for (const k of doomed) {
      storage.removeItem(k);
      removed.push(k);
    }
  } catch {
    // unavailable storage — nothing to sweep
  }
  return removed;
}
